import React, { useEffect, useMemo, useState } from "react";
import { Chart } from "react-chartjs-2";
import { Row, Col, Card, CardBody, CardHeader } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

// 단위 변환 함수 - 숫자 값을 받아서 적절한 단위로 변환하여 문자열로 반환
const formatMoneyText = (value, unit = "USD") => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return "-";
  }

  const abs = Math.abs(number);
  let scaled = number;
  let suffix = "";

  if (abs >= 1e12) {
    scaled = number / 1e12;
    suffix = "조";
  } else if (abs >= 1e8) {
    scaled = number / 1e8;
    suffix = "억";
  } else if (abs >= 1e6) {
    scaled = number / 1e6;
    suffix = "백만";
  } else if (abs >= 1e3) {
    scaled = number / 1e3;
    suffix = "천";
  }

  return `${unit} ${scaled.toFixed(2)}${suffix}`;
};

// 스케일링 계산 함수 - 값들의 배열을 받아서 적절한 스케일링 단위와 나눌 값을 반환
const getValueScale = (values) => {
  const maxAbs = Math.max(...values.map((value) => Math.abs(value || 0)), 0);

  if (maxAbs >= 1e12) {
    return { divisor: 1e12, unit: "조" };
  }
  if (maxAbs >= 1e8) {
    return { divisor: 1e8, unit: "억" };
  }
  if (maxAbs >= 1e6) {
    return { divisor: 1e6, unit: "백만" };
  }
  if (maxAbs >= 1e3) {
    return { divisor: 1e3, unit: "천" };
  }

  return { divisor: 1, unit: "" };
};

// x축 레이블 생성 함수 - 발표일을 연도로 변환하여 표시하되, 연도가 변경되는 시점에만 레이블을 표시
const buildXAxisTick = (labels, index) => {
  const current = new Date(labels[index]).getFullYear(); 
  const previous = index > 0 ? new Date(labels[index - 1]).getFullYear() : null;
  return current !== previous ? String(current) : "";
};

const getHomeFsBarThickness = (viewportWidth) => {
  if (viewportWidth <= 390) return 9;
  if (viewportWidth <= 430) return 10;
  if (viewportWidth <= 575) return 11;
  if (viewportWidth <= 767) return 13;
  if (viewportWidth <= 991) return 14;
  return 18;
};

const HOME_FS_DESKTOP_BREAKPOINT = 992;
const HOME_FS_AUTO_ROLL_INTERVAL_MS = 3000;

// 차트 데이터 모델 생성 함수 - 매출, 영업이익, 이익률을 포함한 모델 반환
const buildCompanyChartModel = (items, barThickness = 18) => {
  const rows = [...items]
    .filter((item) => item?.tgdate)
    .map((item) => ({
      ...item,
      revenue: Number(item?.revenue) || 0,
      netIncome: Number(item?.netIncome) || 0,
      ratio: Number(item?.operatingIncomeRatio),
    }))
    .filter((item) => {
      return !(item.revenue === 0 && item.netIncome === 0);
    })
    .sort((a, b) => new Date(a.tgdate) - new Date(b.tgdate));

  if (rows.length === 0) {
    return {
      latestItem: null,
      chartData: null,
      chartMeta: {
        labels: [],
        unit: "",
        beginLeftAxisAtZero: true,
        beginRightAxisAtZero: true,
        ratioMin: 0,
        ratioMax: 0,
      },
    };
  }

  const labels = rows.map((item) => item.tgdate); // x축 레이블로 발표일 사용
  const revenues = rows.map((item) => item.revenue); // 매출 데이터 배열
  const netIncome = rows.map((item) => item.netIncome); // 순이익 데이터 배열
  const profitRatios = rows.map((item) => (Number.isNaN(item.ratio) ? null : item.ratio)); // 이익률 데이터 배열 (숫자가 아닌 경우 null로 처리)
  const { divisor, unit } = getValueScale([...revenues, ...netIncome]); // 매출과 순이익을 모두 고려하여 적절한 단위와 나눌 값을 결정
  const scaledRevenues = revenues.map((value) => value / divisor);
  const scaledNetIncome = netIncome.map((value) => value / divisor);
  const validRatios = profitRatios.filter((value) => value !== null);

  // 차트 데이터와 메타 정보를 반환하는 객체 생성
  return {
    latestItem: rows[rows.length - 1],
    chartData: {
      labels,
      datasets: [
        {
          type: "bar",
          label: `매출(${unit || "원"})`,
          data: scaledRevenues,
          backgroundColor: "rgba(210, 210, 210, 0.92)",
          borderColor: "rgba(190, 190, 190, 1)",
          borderWidth: 0,
          yAxisID: "y",
          order: 2,
          grouped: false,
          barThickness,
          maxBarThickness: barThickness,
        },
        {
          type: "bar",
          label: "순이익",
          data: scaledNetIncome,
          backgroundColor: netIncome.map((value, index) => {
            if (index === 0) {
              return "#0F6FE0";
            }

            return value > netIncome[index - 1] ? "#DD041E" : "#0F6FE0";
          }),
          borderColor: netIncome.map((value, index) => {
            if (index === 0) {
              return "#0F6FE0";
            }

            return value > netIncome[index - 1] ? "#DD041E" : "#0F6FE0";
          }),
          borderWidth: 0,
          yAxisID: "y",
          order: 1,
          grouped: false,
          barThickness,
          maxBarThickness: barThickness,
        },
        {
          type: "line",
          label: "이익률(%)",
          data: profitRatios,
          borderColor: "rgb(255, 69, 0)",
          backgroundColor: "rgb(255, 69, 0)",
          borderWidth: 2.5,
          yAxisID: "y1",
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 3,
          spanGaps: true,
          order: 0,
        },
      ],
    },
    chartMeta: {
      labels,
      unit,
      beginLeftAxisAtZero: Math.min(...scaledNetIncome, ...scaledRevenues) >= 0,
      beginRightAxisAtZero: (validRatios[0] ?? 0) >= 0,
      ratioMin: validRatios.length ? Math.min(...validRatios) : 0,
      ratioMax: validRatios.length ? Math.max(...validRatios) : 0,
    },
  };
};

function CompanyFsExtractSlide({ group, barThickness }) {
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
  const { latestItem, chartData, chartMeta } = useMemo(
    () => buildCompanyChartModel(group.items, barThickness),
    [group.items, barThickness]
  );
  
  // 커스텀 범례 아이템 정의 (매출, 순이익, 이익률) - 차트 옵션에서 사용
  const customLegendItems = [
    {
      label: `매출(${chartMeta.unit || "원"})`,
      type: "box",
      color: "rgba(210, 210, 210, 0.92)",
      borderColor: "rgba(190, 190, 190, 1)",
    },
    {
      label: "순이익",
      type: "split",
      leftColor: "#0F6FE0",
      rightColor: "#DD041E",
    },
    {
      label: "이익률(%)",
      type: "box",
      color: "rgb(255, 69, 0)",
    },
  ];

  // 최근 실적 소식 섹션에서 회사명과 티커를 매핑하기 위한 객체 생성
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: chartColors.tooltipBg,
        titleColor: chartColors.tooltipTitle,
        bodyColor: chartColors.tooltipBody,
        borderColor: chartColors.tooltipBorder,
        borderWidth: 1,
        callbacks: {
          title: (tooltipItems) => tooltipItems[0]?.label || "",
          label: (context) => {
            if (context.dataset.yAxisID === "y1") {
              return `${context.dataset.label}: ${Number(context.parsed.y).toFixed(2)}%`;
            }

            const unitLabel = chartMeta.unit || "원";
            return `${context.dataset.label}: ${Number(context.parsed.y).toFixed(1)}${unitLabel}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          color: chartColors.borderColor,
        },
        ticks: {
          color: chartColors.tickColor,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          callback: (value, index) => buildXAxisTick(chartMeta.labels, index),
        },
      },
      y: {
        type: "linear",
        position: "left",
        beginAtZero: chartMeta.beginLeftAxisAtZero,
        grid: {
          color: chartColors.gridColor,
        },
        border: {
          color: chartColors.borderColor,
        },
        ticks: {
          color: chartColors.tickColor,
          maxTicksLimit: 6,
          callback: (value) => `${value}${chartMeta.unit}`,
        },
      },
      y1: {
        type: "linear",
        position: "right",
        beginAtZero: chartMeta.beginRightAxisAtZero,
        suggestedMin: chartMeta.ratioMin < 0 ? Math.floor(chartMeta.ratioMin * 10) / 10 : 0,
        suggestedMax: chartMeta.ratioMax > 0 ? Math.ceil(chartMeta.ratioMax * 10) / 10 : 1,
        grid: {
          drawOnChartArea: false,
        },
        border: {
          color: chartColors.borderColor,
        },
        ticks: {
          color: chartColors.tickColor,
          maxTicksLimit: 6,
          callback: (value) => `${value}%`,
        },
      },
    },
  }), [chartColors, chartMeta]);

  return (
    <Card className="mb-0 h-100">
      <CardHeader className="border-0 bg-transparent pb-0">
        <div className="d-flex justify-content-between align-items-start gap-3 home-fs-slide-header">
          <div className="home-fs-slide-title-wrap">
            <div className="fw-bold fs-5 home-fs-slide-title">{group.companyKor} ({group.ticker})</div>
            <div className="text-secondary home-fs-slide-meta">{group.nickname} | {latestItem.filed}</div>
          </div>
          <button
            type="button"
            className="badge border border-primary text-primary bg-transparent"
            onClick={() => navigate(`/stock/${group.ticker}`)}
          >
            더보기
          </button>
        </div>
      </CardHeader>
      <CardBody className="home-fs-slide-body">
        <div
          className="home-fs-summary-box rounded border p-3 mb-3"
          style={{
            backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#f7f7f7",
            borderColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
          }}
        >
          <div className="home-fs-summary-row mb-2">
            <span className="badge bg-secondary border home-fs-summary-badge">매출</span>
            <span className="text-secondary home-fs-summary-value">
              {formatMoneyText(latestItem?.revenue, latestItem?.unit || "USD")}
            </span>
            <span className="text-secondary home-fs-summary-label">
              전년동기대비
            </span>
            <span className={`${latestItem?.revenueCompare4 >= 0 ? "text-danger" : "text-primary"} fw-bold home-fs-summary-change`}>
              {Number(latestItem?.revenueCompare4).toFixed(2) >= 0 ? "+" : ""}{Number(latestItem?.revenueCompare4).toFixed(2) }%
            </span>
          </div>
          <div className="home-fs-summary-row">
            <span className="badge text-primary border home-fs-summary-badge" style={{ backgroundColor: "#eef4ff" }}>순이익</span>
            <span className="text-secondary home-fs-summary-value">
              {formatMoneyText(latestItem?.netIncome, latestItem?.unit || "USD")}
            </span>
            <span className="text-secondary home-fs-summary-label">
              전년동기대비
            </span>
            <span className={`${latestItem?.netIncomeCompare4 >= 0 ? "text-danger" : "text-primary"} fw-bold home-fs-summary-change`}>
              {Number(latestItem?.netIncomeCompare4).toFixed(2) >= 0 ? "+" : ""}{Number(latestItem?.netIncomeCompare4).toFixed(2) }%
            </span>
          </div>
        </div>

        {chartData ? (
          <>
            <div className="d-flex justify-content-end align-items-center flex-wrap gap-3 mb-2">
              {customLegendItems.map((item) => (
                <div key={item.label} className="d-inline-flex align-items-center gap-2 small" style={{ color: chartColors.legendColor }}>
                  {item.type === "split" ? (
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        display: "inline-block",
                        background: `linear-gradient(to right, ${item.leftColor} 0%, ${item.leftColor} 50%, ${item.rightColor} 50%, ${item.rightColor} 100%)`,
                        borderRadius: 2,
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        display: "inline-block",
                        backgroundColor: item.color,
                        border: `1px solid ${item.borderColor || item.color}`,
                        borderRadius: 2,
                      }}
                    />
                  )}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            <div style={{ height: 240 }}>
              <Chart type="bar" data={chartData} options={chartOptions} />
            </div>
          </>
        ) : (
          <div className="text-secondary fs-sm">차트 데이터가 없습니다.</div>
        )}
      </CardBody>
    </Card>
  );
}

export default function HomeCompanyFsExtractSection({ data, companyMetaByTicker }) {
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === "undefined") {
      return 1200;
    }
    return window.innerWidth;
  });
  const barThickness = useMemo(() => getHomeFsBarThickness(viewportWidth), [viewportWidth]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const groupedEntries = useMemo(() => {
    const companyFsExtracts = Array.isArray(data) ? data : [];
    const groupedCompanyFsExtracts = companyFsExtracts.reduce((acc, item) => {
      const ticker = item?.ticker;
      if (!ticker) {
        return acc;
      }

      if (!acc[ticker]) {
        acc[ticker] = [];
      }

      acc[ticker].push(item);
      return acc;
    }, {});

    return Object.entries(groupedCompanyFsExtracts).map(([ticker, items]) => ({
      ticker,
      company: items[0]?.company || ticker,
      companyKor: companyMetaByTicker[ticker]?.companyKor || "",
      nickname: companyMetaByTicker[ticker]?.nickname || "",
      items,
    }));
  }, [data, companyMetaByTicker]);

  const canAutoplay = groupedEntries.length > 1;
  const canLoop = groupedEntries.length > 2;
  const desktopSpaceBetween = 16;
  const tabletSpaceBetween = 10;
  const mobileSpaceBetween = 8;

  return (
    <Row>
      <Col>
        <Card>
          <CardBody>
            <div className="home-section-header">
              <div>
                <div className="home-section-title">최근 실적 소식을 한 눈에</div>
                <div className="home-section-subtitle">매출, 영업이익, 이익률을 최근 발표일 기준으로 봅니다.</div>
              </div>
            </div>
          </CardBody>
          <CardBody className="pt-0 home-fs-content-body">
            {groupedEntries.length === 0 ? (
              <div className="text-secondary fs-sm">표시할 실적 카드가 없습니다.</div>
            ) : (
              <Swiper
                modules={[Autoplay]}
                className="home-fs-swiper pb-1"
                slidesPerView={1}
                slidesPerGroup={1}
                spaceBetween={mobileSpaceBetween}
                breakpoints={{
                  576: {
                    spaceBetween: tabletSpaceBetween,
                  },
                  [HOME_FS_DESKTOP_BREAKPOINT]: {
                    slidesPerView: 2,
                    slidesPerGroup: 2,
                    spaceBetween: desktopSpaceBetween,
                  },
                }}
                loop={canLoop}
                rewind={!canLoop && canAutoplay}
                speed={500}
                grabCursor={canAutoplay}
                allowTouchMove={canAutoplay}
                watchOverflow
                autoplay={canAutoplay
                  ? {
                    delay: HOME_FS_AUTO_ROLL_INTERVAL_MS,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: true,
                  }
                  : false
                }
              >
                {groupedEntries.map((group) => (
                  <SwiperSlide key={group.ticker} className="home-fs-swiper-slide">
                    <CompanyFsExtractSlide group={group} barThickness={barThickness} />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}
